import React, { useEffect, useRef, useState } from "react";
import Styles from "./CallPopup.module.scss";
import Typography from "@vahak/core-ui/dist/components/Typography";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import IconWrapper from "@vahak/core-ui/dist/components/IconWrapper";
import { CallInfo } from "../../context/call/BrowerClickToCallProviderOld";
import { convertSecondsTime } from "../../methods/time";
import AppImg from "../utils/AppImg";
import { getImageNameFromURL } from "../../utils/getImageNameFromURL";
import Flex, { VerticalFlex } from "@vahak/core-ui/dist/layout/Flex";
import DraggableWrapper from "@vahak/core-ui/dist/components/common/DraggableWrapper/DraggableWrapper";
import AcceptIcon from "@vahak/core/dist/icons/call-accept.svg";
import MicIcon from "@vahak/core/dist/icons/call-mic.svg";
import MicMuteIcon from "@vahak/core/dist/icons/call-mic-muted.svg";
import RejectIcon from "@vahak/core/dist/icons/call-reject.svg";
import PlusIcon from "@vahak/core/dist/standard-icons/plus-circle-white.svg";

interface CallPopupProps {
    onHangup?: () => void;
    onReject?: () => void;
    onToggleMute?: () => void;
    onAnswer?: () => void;
    onClickAddToConference?: () => void;
    callInfo?: CallInfo;
}

const CallPopup = ({
    onHangup,
    onToggleMute,
    callInfo,
    onAnswer,
    onReject,
    onClickAddToConference
}: CallPopupProps) => {
    const { startTime, endTime, isCallActive } = callInfo || {};
    const timerId = useRef<ReturnType<typeof setInterval> | null>(null);
    const [duration, setDuration] = useState(0);
    const showMuteBtn = callInfo?.isConnected;

    const { m, s, h } = convertSecondsTime(duration);
    const hour = h.toString().padStart(2, "0");
    const min = m.toString().padStart(2, "0");
    const sec = s.toString().padStart(2, "0");

    const callDurationFormattedText = `${h ? `${hour}:` : ""}${min}:${sec}`;

    const updateDuration = () => {
        if (startTime) {
            const A = startTime.getTime();
            const B = endTime?.getTime?.() || Date.now();
            const duration = Math.ceil((B - A) / 1000);
            setDuration(duration);
        }
    };

    useEffect(() => {
        if (!startTime) return;

        const shouldTrack = isCallActive && !endTime;

        // Update once immediately
        updateDuration();

        if (shouldTrack) {
            timerId.current = setInterval(updateDuration, 1000);
        }

        // Cleanup function
        return () => {
            if (timerId.current) {
                clearInterval(timerId.current);
                timerId.current = null;
            }
        };
    }, [startTime, endTime, isCallActive]);

    return (
        <>
            <DraggableWrapper
                initialStyle={{
                    left: "50%",
                    top: "80%",
                    transform: "translateX(-50%)"
                }}
            >
                <div className={Styles.main}>
                    <Flex gap={20} justifyContent="space-between">
                        <div className={Styles.person}>
                            <AppImg
                                src={getImageNameFromURL(callInfo?.calleInfomation?.imgUrl || `default.png`)}
                                alt={""}
                                width={"60"}
                                height={"60"}
                                className={Styles.personImg}
                            />
                            <VerticalFlex gap={8}>
                                <Typography weight="semibold" size="sm" color={COLORS.WHITE}>
                                    {callInfo?.calleInfomation?.name}
                                </Typography>
                            </VerticalFlex>
                        </div>

                        <div className={Styles.action}>
                            {showMuteBtn && (
                                <IconWrapper width={50} height={50} onClick={onToggleMute}>
                                    {callInfo?.isMuted ? <MicMuteIcon /> : <MicIcon />}
                                </IconWrapper>
                            )}
                            {!!(onHangup || onReject) && (
                                <IconWrapper width={50} height={50} onClick={onHangup ? onHangup : onReject}>
                                    {<RejectIcon />}
                                </IconWrapper>
                            )}

                            {!!onAnswer && (
                                <IconWrapper width={50} height={50} onClick={onAnswer}>
                                    {<AcceptIcon />}
                                </IconWrapper>
                            )}
                            {!!onClickAddToConference && (
                                <IconWrapper width={50} height={50} onClick={onClickAddToConference}>
                                    <PlusIcon />
                                </IconWrapper>
                            )}
                        </div>
                    </Flex>

                    <Flex gap={20}>
                        {callInfo?.startTime ? (
                            <Typography weight="bold" color={COLORS.RED} size="s">
                                {callDurationFormattedText}
                            </Typography>
                        ) : null}
                        <Typography size="s" color={COLORS.WHITE}>
                            {callInfo?.callDesc}
                        </Typography>
                    </Flex>
                </div>
            </DraggableWrapper>
        </>
    );
};

export default CallPopup;
